import { getScoreColor, getScoreLabel, getScoreRingColor } from '../../utils/scoreHelpers';

function getPointsToNextLevel(score) {
  if (score >= 90) return 0;
  if (score >= 80) return 90 - score;
  if (score >= 70) return 80 - score;
  if (score >= 60) return 70 - score;
  return 60 - score;
}

export default function ScoreGauge({ overallScore, level, percentileRank, pointsToNextLevel: _unused, personalityTags }) {
  const circumference = 2 * Math.PI * 70;
  const offset = circumference - (circumference * overallScore) / 100;
  const ringColor = getScoreRingColor(overallScore);
  const pointsToNextLevel = getPointsToNextLevel(overallScore);

  return (
    <div className="card text-center">
      <h2 className="text-lg font-semibold mb-6">Your Handwriting Score</h2>

      <div className="relative inline-flex items-center justify-center mb-4">
        <svg className="w-44 h-44 transform -rotate-90" viewBox="0 0 160 160">
          <circle cx="80" cy="80" r="70" stroke="#E5E5E5" strokeWidth="12" fill="none" />
          <circle
            cx="80" cy="80" r="70"
            stroke={ringColor}
            strokeWidth="12"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-5xl font-bold ${getScoreColor(overallScore)}`}>
            {overallScore}
          </span>
          <span className="text-bastion-gray-700 text-sm font-medium">
            {level || getScoreLabel(overallScore)}
          </span>
        </div>
      </div>

      {/* Percentile and next level */}
      <div className="flex items-center justify-center gap-6 mb-4 text-sm">
        {percentileRank && (
          <div className="text-bastion-gray-800">
            <span className="font-semibold text-bastion-black">{percentileRank}th</span> percentile
          </div>
        )}
        {pointsToNextLevel > 0 && (
          <div className="text-bastion-gray-800">
            <span className="font-semibold text-bastion-black">{pointsToNextLevel}</span> pts to next level
          </div>
        )}
      </div>

      {/* Score tiers explanation */}
      <div className="bg-bastion-gray-50 rounded-lg px-4 py-3 mb-4 text-xs text-bastion-gray-700 max-w-sm mx-auto">
        <p className="mb-2 font-semibold text-bastion-black text-sm">Score Tiers</p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-left">
          <div><span className="font-semibold text-green-600">90–100</span> Excellent</div>
          <div><span className="font-semibold text-emerald-600">80–89</span> Great</div>
          <div><span className="font-semibold text-yellow-600">70–79</span> Good</div>
          <div><span className="font-semibold text-orange-500">60–69</span> Fair</div>
          <div><span className="font-semibold text-red-500">Below 60</span> Needs Work</div>
        </div>
        {percentileRank && (
          <p className="mt-2 text-bastion-gray-800 border-t border-bastion-gray-200 pt-2">
            Your score is in the <span className="font-semibold text-bastion-black">{percentileRank}th percentile</span> — better than {percentileRank}% of all writers analyzed.
          </p>
        )}
      </div>

      {/* Personality Tags */}
      {personalityTags && personalityTags.length > 0 && (
        <div className="flex items-center justify-center gap-2 flex-wrap">
          {personalityTags.map((tag, i) => (
            <span
              key={i}
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-bastion-blue-50 text-bastion-black"
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
